import { useCallback, useEffect, useRef, useState } from 'react';
import type { Message } from '../types';
import type { WidgetStrings } from '../utils/i18n';
import { formatCount, formatWidgetString } from '../utils/i18n';

/** How often a "still responding" cue is spoken while a reply streams. */
export const STREAM_PROGRESS_INTERVAL_MS = 8_000;
/** Progress cues per stream; long replies go quiet after this many. */
export const STREAM_MAX_PROGRESS_CUES = 3;
/** Replies up to this length are read in full with the completion cue. */
export const SHORT_REPLY_MAX_CHARS = 280;
/** How long a cue stays in the region before it is emptied (see `announce`). */
export const ANNOUNCEMENT_CLEAR_MS = 1_000;

export interface UseStreamingAnnouncementsOptions {
  /** True while a reply is streaming into the log. */
  isStreaming: boolean;
  /** The text streamed so far; empty once the stream ends. */
  streamedText: string;
  /** True from send until the request settles (outlives the stream briefly). */
  isTyping: boolean;
  /** Id of the message currently streaming; the finalised bubble reuses it. */
  streamingMessageId?: string | null;
  /**
   * The log's messages. On the render where the stream ends the finalised message
   * is already here, so the completion cue can mention product cards it carries.
   */
  messages?: readonly Message[];
  strings: WidgetStrings;
  /** Master switch; when false the hook is inert and never suppresses typing. */
  enabled?: boolean;
}

export interface StreamingAnnouncements {
  /** Text for the dedicated `role="status"` region; empty when nothing is queued. */
  announcement: string;
  /**
   * True from the start cue until the turn settles (`isTyping` false), so the
   * TypingIndicator does not re-announce "Agent is typing" after the reply.
   */
  suppressTypingCue: boolean;
}

function countWords(text: string): number {
  return text.split(/\s+/).filter(Boolean).length;
}

/**
 * Measured screen-reader cues for a streamed reply. The message log is
 * `aria-live="polite" aria-relevant="additions"`, which ignores text mutations
 * while a reply streams and then reads the whole finalised bubble at once. This
 * hook instead speaks: one "in progress" cue when the stream starts, at most
 * `STREAM_MAX_PROGRESS_CUES` "still responding" cues every
 * `STREAM_PROGRESS_INTERVAL_MS`, and one completion cue that includes the reply
 * text when it is short (<= `SHORT_REPLY_MAX_CHARS`) or a word count otherwise,
 * plus how many product cards the finished message carries (if any).
 */
export function useStreamingAnnouncements({
  isStreaming,
  streamedText,
  isTyping,
  streamingMessageId,
  messages,
  strings,
  enabled = true,
}: UseStreamingAnnouncementsOptions): StreamingAnnouncements {
  const [announcement, setAnnouncement] = useState('');
  const [suppressTypingCue, setSuppressTypingCue] = useState(false);
  const stringsRef = useRef(strings);
  const lastTextRef = useRef('');
  const wasStreamingRef = useRef(false);
  const lastStreamIdRef = useRef<string | null>(null);
  const messagesRef = useRef(messages);
  const clearTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressTimerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  useEffect(() => {
    stringsRef.current = strings;
  }, [strings]);

  // Keep the latest streamed text so the completion cue can use it on the render
  // where the streaming node has already been swapped out (streamedText is '' then).
  useEffect(() => {
    if (isStreaming) lastTextRef.current = streamedText;
  }, [isStreaming, streamedText]);

  // Same idea for the id (cleared with the streaming node) and the message list
  // (read by the completion effect below without re-running it on every change).
  useEffect(() => {
    if (isStreaming && streamingMessageId) lastStreamIdRef.current = streamingMessageId;
  }, [isStreaming, streamingMessageId]);
  useEffect(() => {
    messagesRef.current = messages;
  }, [messages]);

  const announce = useCallback((text: string) => {
    if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    setAnnouncement(text);
    // A live region only speaks when its content changes, so setting the same
    // string twice in a row ("Response in progress." on consecutive turns) would be
    // silent. Emptying the region shortly after each cue is the standard live
    // announcer pattern: the screen reader has already queued the text, and the
    // next identical cue is a fresh insertion again. Clearing on a delay rather
    // than the next tick avoids readers that debounce DOM mutations missing it.
    clearTimerRef.current = setTimeout(() => {
      clearTimerRef.current = null;
      setAnnouncement('');
    }, ANNOUNCEMENT_CLEAR_MS);
  }, []);

  const stopProgressCues = useCallback(() => {
    if (progressTimerRef.current) {
      clearInterval(progressTimerRef.current);
      progressTimerRef.current = null;
    }
  }, []);

  useEffect(() => {
    const wasStreaming = wasStreamingRef.current;
    wasStreamingRef.current = isStreaming;
    if (!enabled) return;

    if (isStreaming && !wasStreaming) {
      // lastTextRef is not reset here: the tracking effect above has already
      // recorded this render's text, which may be non-empty if the stream's first
      // chunk and its start land in the same render.
      setSuppressTypingCue(true);
      announce(stringsRef.current.responseStarted);
      let cues = 0;
      stopProgressCues();
      progressTimerRef.current = setInterval(() => {
        cues += 1;
        announce(stringsRef.current.responseProgress);
        if (cues >= STREAM_MAX_PROGRESS_CUES) stopProgressCues();
      }, STREAM_PROGRESS_INTERVAL_MS);
    } else if (!isStreaming && wasStreaming) {
      stopProgressCues();
      const text = lastTextRef.current.trim();
      const s = stringsRef.current;
      const streamId = lastStreamIdRef.current;
      const finished = streamId
        ? messagesRef.current?.find(message => message.id === streamId)
        : undefined;
      const productCount = Array.isArray(finished?.richContent?.products)
        ? finished.richContent.products.length
        : 0;
      const productsCue =
        productCount > 0
          ? ` ${formatWidgetString(s.responseIncludesProducts, {
              products: formatCount(s, 'productRecommendationCount', productCount),
            })}`
          : '';
      if (!text) announce(`${s.responseComplete}${productsCue}`);
      else if (text.length <= SHORT_REPLY_MAX_CHARS)
        announce(`${s.responseComplete} ${text}${productsCue}`);
      else
        announce(
          `${formatWidgetString(s.responseCompleteLong, { words: countWords(text) })}${productsCue}`
        );
      lastTextRef.current = '';
      lastStreamIdRef.current = null;
    }
  }, [isStreaming, enabled, announce, stopProgressCues]);

  // The turn is over once neither streaming nor typing: let the next turn's
  // TypingIndicator announce again.
  useEffect(() => {
    if (!isTyping && !isStreaming) setSuppressTypingCue(false);
  }, [isTyping, isStreaming]);

  useEffect(
    () => () => {
      stopProgressCues();
      if (clearTimerRef.current) clearTimeout(clearTimerRef.current);
    },
    [stopProgressCues]
  );

  return { announcement, suppressTypingCue };
}
